import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
	memo,
	useMemo,
} from "react";
import {
	Box,
	Typography,
	Paper,
	Grid,
	CircularProgress,
	Button,
	Chip,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import FilterSection from "../misc/FilterSection";

// Register ChartJS components
ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ChartDataLabels,
);

// Horizontal zoom for the stacked bar chart.
//
// The chart lays each category out over a fixed pixel width and scrolls
// horizontally when the categories don't fit. Zoom simply scales that
// per-category width: smaller width => more categories visible at once
// (compact / "Fit all"), larger width => fewer, wider bars for inspection.
// Nothing about the data, categories, order, colours or values changes.
const BASE_BAR_WIDTH = 150; // px per category at 100% zoom (Fit-All minimum is enforced separately by MIN_SLOT_PX)
const MIN_ZOOM = 20; // lower bound — Fit All floors here to show the maximum practical number of SE-Team fields
const MAX_ZOOM = 300;
const ZOOM_STEP = 20;
const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

// Word-based wrapping for the diagonal SE-Team labels: splits the name into
// 2-3 balanced lines at word boundaries only (never mid-word, so there are
// never single-letter lines). A single long word is kept whole on its line.
function vpWrapLabel(text) {
	const words = String(text).split(/\s+/).filter(Boolean);
	if (words.length <= 1) return String(text);
	const totalLen = words.reduce((s, w) => s + w.length, 0) + (words.length - 1);
	// Cap at 2 lines: a 3-line diagonal block is too thick and its neighbours
	// touch even at wide slots; 2 lines keeps each block thin so labels stay
	// clearly separated. Long names simply use two longer lines (still whole words).
	const maxLines = 2;
	const target = Math.ceil(totalLen / maxLines);
	const lines = [];
	let current = "";
	for (const w of words) {
		if (!current) {
			current = w;
		} else if (current.length + 1 + w.length <= target || lines.length === maxLines - 1) {
			current += " " + w;
		} else {
			lines.push(current);
			current = w;
		}
	}
	if (current) lines.push(current);
	for (let k = lines.length - 1; k > 0; k--) {
		if (lines[k].length < 3) {
			lines[k - 1] += " " + lines[k];
			lines.splice(k, 1);
		}
	}
	if (lines.length > 1 && lines[0].length < 3) {
		lines[1] = lines[0] + " " + lines[1];
		lines.shift();
	}
	return lines.join("\n");
}

// Shared chart options
const baseChartOptions = {
	responsive: true,
	maintainAspectRatio: false,
	layout: { padding: { top: 10, bottom: 10, left: 10, right: 10 } },
	plugins: {
		legend: {
			position: "top",
			align: "start",
			labels: {
				font: { family: "MAN EUROPE_Bold", size: 12 },
				usePointStyle: true,
				padding: 20,
				boxWidth: 12,
			},
			onHover: (e) => {
				e.native.target.style.cursor = "pointer";
			},
			onLeave: (e) => {
				e.native.target.style.cursor = "default";
			},
		},
		datalabels: { display: false },
	},
};

const SupplyTypeAllocationDashboard = memo(
	({
		totalParts,
		purchaseTypeCounts,
		seTeamCounts,
		mainChartData: rawChartData,
		filterOptions,
		selectedTeams,
		selectedProject,
		selectedProgramm,
		selectedPurchaseType,
		selectedCategory,
		tempSelectedTeams,
		tempSelectedProject,
		tempSelectedProgramm,
		tempSelectedPurchaseType,
		tempSelectedCategory,
		handleTeamChange,
		handleProjectChange,
		handleProgrammChange,
		handlePurchaseTypeChange,
		handleCategoryChange,
		handleApplyFilters,
		handleResetFilters,
		isChartLoading,
		hasFiltersChanged,
	}) => {
		// Modal state for chart click
		const [modalOpen, setModalOpen] = useState(false);
		const [modalData, setModalData] = useState({
			seTeam: "",
			purchaseType: "",
			count: 0,
		});

		// Reorder chart data so the "SET Allocation Pending" ("-") team is always
		// the first column from the left. The "Unknown" / "unbekannt" columns are
		// also folded into this pending column (their per-purchase-type values are
		// summed in). This is the "real" data set: values here are the true part
		// counts and are used for every label, tooltip and grand total so the
		// numbers stay accurate.
		const orderedChartData = useMemo(() => {
			if (!rawChartData || !rawChartData.labels || !rawChartData.datasets) {
				return rawChartData;
			}

			// Labels that should be collapsed into the "SET Allocation Pending"
			// column (the "-" label). Matched case-insensitively after trimming.
			const isPendingLabel = (label) => {
				const normalized = String(label).trim().toLowerCase();
				return (
					normalized === "-" ||
					normalized === "unknown" ||
					normalized === "unbekannt"
				);
			};

			const pendingIndices = [];
			const otherIndices = [];
			rawChartData.labels.forEach((label, index) => {
				if (isPendingLabel(label)) {
					pendingIndices.push(index);
				} else {
					otherIndices.push(index);
				}
			});

			// No pending/unknown columns present — keep the data as-is.
			if (pendingIndices.length === 0) {
				return rawChartData;
			}

			// The merged pending column is always rendered first and labelled "-"
			// so `formatTeamName` displays "SET Allocation Pending".
			const labels = ["-", ...otherIndices.map((i) => rawChartData.labels[i])];

			const datasets = rawChartData.datasets.map((dataset) => {
				const mergedPendingValue = pendingIndices.reduce(
					(sum, i) => sum + (dataset.data[i] || 0),
					0,
				);
				return {
					...dataset,
					data: [
						mergedPendingValue,
						...otherIndices.map((i) => dataset.data[i]),
					],
				};
			});

			return { ...rawChartData, labels, datasets };
		}, [rawChartData]);

		// The largest real grand total across every column (e.g. 126000). The
		// "SET Allocation Pending" column is scaled up to this height so it is
		// rendered as tall as the biggest column while all other columns keep their
		// normal (real) heights and dynamic scaling behaviour.
		const globalMaxGrandTotal = useMemo(() => {
			if (!orderedChartData?.labels) return 0;
			let max = 0;
			orderedChartData.labels.forEach((_, index) => {
				const total = orderedChartData.datasets.reduce(
					(sum, dataset) => sum + (dataset.data[index] || 0),
					0,
				);
				if (total > max) max = total;
			});
			return max;
		}, [orderedChartData]);

		// Display data passed to the chart: identical to the real data except the
		// pending column (index 0) is inflated so its stacked height equals the
		// tallest column. Proportions between purchase types within the column are
		// preserved. Labels/tooltips map back to the real values via
		// `orderedChartData`, so only the *visual bar height* is affected.
		const mainChartData = useMemo(() => {
			if (!orderedChartData || !orderedChartData.labels) {
				return orderedChartData;
			}

			const pendingIndex = orderedChartData.labels.findIndex(
				(label) => label === "-",
			);
			if (pendingIndex === -1 || globalMaxGrandTotal <= 0) {
				return orderedChartData;
			}

			const pendingRealTotal = orderedChartData.datasets.reduce(
				(sum, dataset) => sum + (dataset.data[pendingIndex] || 0),
				0,
			);
			// Nothing to inflate (empty column or already the tallest).
			if (pendingRealTotal <= 0 || pendingRealTotal >= globalMaxGrandTotal) {
				return orderedChartData;
			}

			const scaleFactor = globalMaxGrandTotal / pendingRealTotal;

			return {
				...orderedChartData,
				datasets: orderedChartData.datasets.map((dataset) => {
					const data = dataset.data.slice();
					data[pendingIndex] = (data[pendingIndex] || 0) * scaleFactor;
					return { ...dataset, data };
				}),
			};
		}, [orderedChartData, globalMaxGrandTotal]);

		// Normalize SE-team display name: "-" → "SET Allocation Pending"
		const formatTeamName = (name) =>
			name === "-" ? "SET Allocation Pending" : name;

		// Chart click handler to open modal with supply type details
		const handleChartClick = (event, elements) => {
			if (!elements || elements.length === 0) return;

			const element = elements[0];
			const datasetIndex = element.datasetIndex;
			const index = element.index;

			if (!mainChartData || !mainChartData.datasets || !mainChartData.labels)
				return;

			const dataset = mainChartData.datasets[datasetIndex];
			const purchaseType = dataset.label; // This is the chart segment type (purchase type)
			// Use the REAL count (chart value may be inflated for the pending column).
			const count =
				orderedChartData?.datasets?.[datasetIndex]?.data?.[index] ??
				dataset.data[index]; // Number of parts in this segment

			if (count === 0) return; // Don't open if no parts

			const seTeam = mainChartData.labels[index]; // SE Team name

			// Set modal data and open modal
			setModalData({
				seTeam,
				purchaseType,
				count,
			});
			setModalOpen(true);
		};
		// Function to open Supply Type page in new tab
		const handleOpenSupplyTypePage = () => {
			const { seTeam, purchaseType } = modalData;

			// Determine the target view type based on the active category filter.
			// If the user filtered by a single specific category (VFF or Series) we
			// redirect directly to that view; otherwise default to Prototype.
			let targetViewType = "Prototype";
			if (
				selectedCategory &&
				!selectedCategory.includes("all") &&
				selectedCategory.length === 1
			) {
				const cat = selectedCategory[0].toLowerCase();
				if (cat === "vff") {
					targetViewType = "VFF";
				} else if (cat === "series") {
					targetViewType = "Series";
				}
			}

			// Build the URL with filter parameters for the Supply Type page
			const baseUrl = `${window.location.origin}/supply-type-allocation`;
			const urlParams = new URLSearchParams({
				// Pass the specific filters to apply
				se_team_filter: seTeam,
				purchase_type_filter: purchaseType,
				project_filter:
					selectedProject.length > 0 && !selectedProject.includes("all")
						? selectedProject[0]
						: "",
				programm_filter:
					selectedProgramm.length > 0 && !selectedProgramm.includes("all")
						? selectedProgramm[0]
						: "",
				view_type_filter: targetViewType,
				from_dashboard: "true",
			});

			const supplyTypeUrl = `${baseUrl}?${urlParams.toString()}`;

			// Open in new tab
			window.open(supplyTypeUrl, "_blank");
		};

		// Derive filtered SE-Teams based on selected project
		const filteredSeTeams = useMemo(() => {
			if (tempSelectedProject.includes("all")) {
				return filterOptions?.seTeams || [];
			}

			const projectTeams = [
				...new Set(
					tempSelectedProject.flatMap(
						(project) => filterOptions?.projectTeamMapping?.[project] || [],
					),
				),
			];

			// Debug logging
			console.log("Project selection changed:", {
				tempSelectedProject,
				projectTeamMapping: filterOptions?.projectTeamMapping,
				filteredTeams: projectTeams,
			});

			return projectTeams;
		}, [
			tempSelectedProject,
			filterOptions?.seTeams,
			filterOptions?.projectTeamMapping,
		]);
		// Helper function to get active filters information for display
		const getActiveFiltersInfo = () => {
			const activeFilters = [];

			if (
				selectedProject &&
				selectedProject.length > 0 &&
				!selectedProject.includes("all")
			) {
				activeFilters.push({
					label: "Projects",
					count: selectedProject.length,
					values: selectedProject,
				});
			}

			if (
				selectedProgramm &&
				selectedProgramm.length > 0 &&
				!selectedProgramm.includes("all")
			) {
				activeFilters.push({
					label: "Programs",
					count: selectedProgramm.length,
					values: selectedProgramm,
				});
			}

			if (
				selectedPurchaseType &&
				selectedPurchaseType.length > 0 &&
				!selectedPurchaseType.includes("all")
			) {
				activeFilters.push({
					label: "Purchase Types",
					count: selectedPurchaseType.length,
					values: selectedPurchaseType,
				});
			}
			if (
				selectedTeams &&
				selectedTeams.length > 0 &&
				!selectedTeams.includes("all")
			) {
				activeFilters.push({
					label: "Teams",
					count: selectedTeams.length,
					values: selectedTeams,
				});
			}

			if (
				selectedCategory &&
				selectedCategory.length > 0 &&
				!selectedCategory.includes("all")
			) {
				activeFilters.push({
					label: "Categories",
					count: selectedCategory.length,
					values: selectedCategory,
				});
			}

			return activeFilters;
		};

		const activeFiltersInfo = getActiveFiltersInfo();
		const totalActiveFilters = activeFiltersInfo.reduce(
			(sum, filter) => sum + filter.count,
			0,
		);

		const chartContainerRef = useRef(null);
		const teamLabelsContainerRef = useRef(null);
		const chartRef = useRef(null);
		const [currentScrollPosition, setCurrentScrollPosition] = useState(0);
		const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 });
		const [dynamicMaxValue, setDynamicMaxValue] = useState(100);

		// Horizontal zoom level (percent). Drives the per-category pixel width used
		// by both the chart body and the SE-team label row below it. Purely a view
		// concern — see the BASE_BAR_WIDTH comment above.
		const [zoom, setZoom] = useState(100);
		// Minimum per-category slot = just enough that a 2-line diagonal label
		// (~25px thick) keeps a small, consistent gap from its neighbour at -45deg
		// (slot * sin45 ~ 35px > block + ~10px gap). Kept as small as possible so
		// the maximum number of bars fit in Fit All with minimal scrolling; the
		// gap comes from the label slot only, not from widening the bars.
		const MIN_SLOT_PX = 50;
		const barWidthPx = Math.max(MIN_SLOT_PX, Math.round((BASE_BAR_WIDTH * zoom) / 100));

		// Label font size: 11px readable floor that grows with the per-category
		// width, capped so a wrapped diagonal label still fits the label band.
		const labelFontPx = Math.max(11, Math.min(14, Math.round(9 + barWidthPx / 20)));

		// Widest wrapped line (px @ the 11px reference font) for each label, using the
		// same word-boundary wrapping used to render it (see vpWrapLabel).
		const labelWidthsPx = useMemo(() => {
			if (!mainChartData?.labels?.length) return [];
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			if (!ctx) return [];
			ctx.font = "11px 'MAN EUROPE_Bold'";
			return mainChartData.labels.map((label) => {
				const normalized = label === "-" ? "SET Allocation Pending" : label;
				const title = normalized.replace(/\b\w/g, (c) => c.toUpperCase());
				let widest = 0;
				for (const line of vpWrapLabel(title).split("\n")) {
					const w = ctx.measureText(line).width;
					if (w > widest) widest = w;
				}
				return widest;
			});
		}, [mainChartData]);

		// Dynamically size the label band so the longest diagonal (-45deg) label
		// is always fully visible: the rotated bounding-box height of a text block
		// of width W (widest wrapped line) and height H (line boxes + gaps) is
		// (W + H) * sin45. Add the 8px top offset and a small padding.
		const labelBandHeightPx = useMemo(() => {
			const MIN = 130;
			if (!mainChartData?.labels?.length) return MIN;
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			if (!ctx) return MIN;
			ctx.font = `${labelFontPx}px 'MAN EUROPE_Bold'`;
			const SIN = Math.SQRT1_2; // sin(45deg) === cos(45deg)
			const lineBox = labelFontPx * 1.1; // per-line box height (lineHeight 1.1)
			const lineGap = 4; // marginTop between wrapped lines
			let maxExtent = 0;
			for (const label of mainChartData.labels) {
				const normalized = label === "-" ? "SET Allocation Pending" : label;
				const title = normalized.replace(/\b\w/g, (ch) => ch.toUpperCase());
				const lines = vpWrapLabel(title).split("\n");
				let widest = 0;
				for (const line of lines) {
					const w = ctx.measureText(line).width;
					if (w > widest) widest = w;
				}
				const blockH = lines.length * lineBox + (lines.length - 1) * lineGap;
				const extent = 8 + (widest + blockH) * SIN;
				if (extent > maxExtent) maxExtent = extent;
			}
			return Math.max(MIN, Math.ceil(maxExtent + 14));
		}, [mainChartData, labelFontPx]);

		// The x-axis title strip below the band is a fixed 40px. The card grows
		// with the band so the plot area (and therefore the bars) stays identical.
		const chartBottomReservePx = labelBandHeightPx + 40;
		const cardHeightPx = 430 + chartBottomReservePx;

		// Minimal left space reserved on the chart canvas and the label row so the
		// down-left diagonal labels never clip. cos(45deg) ~ 0.707 projects the
		// widest line horizontally; an allowance for the 2-3 line block height is
		// added, then the label's own left offset is subtracted.
		// Reserve just enough space left of the first bar so the leftmost diagonal
		// label (e.g. "SET Allocation Pending") is fully visible and never clipped.
		// Computed from that label's rotated (-45deg) footprint minus the room already
		// available left of the first bar's centre. Added equally to the chart canvas
		// and the label row, so bars/labels stay aligned and bar width/spacing are
		// unchanged (the plot is only shifted right by this amount).
		const labelLeftReservePx = useMemo(() => {
			if (!mainChartData?.labels?.length) return 0;
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			if (!ctx) return 0;
			ctx.font = `${labelFontPx}px 'MAN EUROPE_Bold'`;
			const first = mainChartData.labels[0];
			const normalized = first === "-" ? "SET Allocation Pending" : first;
			const title = normalized.replace(/\b\w/g, (ch) => ch.toUpperCase());
			const lines = vpWrapLabel(title).split("\n");
			let widest = 0;
			for (const line of lines) {
				const w = ctx.measureText(line).width;
				if (w > widest) widest = w;
			}
			const blockH = lines.length * (labelFontPx * 1.1) + (lines.length - 1) * 4;
			const leftExtent = (widest + blockH) * Math.SQRT1_2;
			const available = 20 + barWidthPx / 2; // 20px row padding + half a slot
			return Math.max(0, Math.ceil(leftExtent - available + 6));
		}, [mainChartData, labelFontPx, barWidthPx]);

		const zoomIn = useCallback(
			() => setZoom((z) => clampZoom(z + ZOOM_STEP)),
			[],
		);
		const zoomOut = useCallback(
			() => setZoom((z) => clampZoom(z - ZOOM_STEP)),
			[],
		);

		// Auto-fit: scale so the categories fill the available width (up to
		// MAX_ZOOM), then jump back to the start. Few categories widen to fill the
		// frame (no large empty space); many categories floor at MIN_ZOOM where the
		// per-slot minimum (MIN_SLOT_PX) keeps bars/labels usable and the rest
		// scrolls. Follows whatever categories are in mainChartData right now.
		const fitAll = useCallback(() => {
			const container = chartContainerRef.current;
			const count = mainChartData?.labels?.length || 0;
			if (!container || count === 0) return;

			// The chart body carries 50px of horizontal padding (20 + 30); subtract
			// it so the categories fit inside the visible area, not under the pad.
			const available = Math.max(container.clientWidth - 50, 1);
			const idealBarWidth = available / count;
			const fitZoom = clampZoom(
				Math.floor((idealBarWidth / BASE_BAR_WIDTH) * 100),
			);

			setZoom(fitZoom);
			container.scrollLeft = 0;
			if (teamLabelsContainerRef.current) {
				teamLabelsContainerRef.current.scrollLeft = 0;
			}
			setCurrentScrollPosition(0);
		}, [mainChartData]);

		// Real grand totals for each team (sum of all stacks). Uses the REAL data so
		// every label, tooltip and drawn grand total shows the true part count.
		const teamGrandTotals = useMemo(() => {
			if (!orderedChartData) return {};

			const totals = {};
			orderedChartData.labels.forEach((teamName, index) => {
				const total = orderedChartData.datasets.reduce((sum, dataset) => {
					return sum + (dataset.data[index] || 0);
				}, 0);
				totals[teamName] = total;
			});
			return totals;
		}, [orderedChartData]);

		// Displayed grand totals (uses the inflated pending column). Only used to
		// size the dynamic y-axis so the inflated pending bar never gets clipped.
		const teamDisplayTotals = useMemo(() => {
			if (!mainChartData) return {};

			const totals = {};
			mainChartData.labels.forEach((teamName, index) => {
				const total = mainChartData.datasets.reduce((sum, dataset) => {
					return sum + (dataset.data[index] || 0);
				}, 0);
				totals[teamName] = total;
			});
			return totals;
		}, [mainChartData]);

		// Calculate visible bars and dynamic max value
		const calculateVisibleRange = useCallback(() => {
			if (!mainChartData || !chartContainerRef.current) return;

			const container = chartContainerRef.current;
			const containerWidth = container.clientWidth;
			const scrollLeft = container.scrollLeft;
			const totalWidth = mainChartData.labels.length * barWidthPx;
			const barWidth = barWidthPx;

			// Calculate which bars are currently visible
			const startIndex = Math.floor(scrollLeft / barWidth);
			const endIndex = Math.min(
				Math.ceil((scrollLeft + containerWidth) / barWidth),
				mainChartData.labels.length,
			);

			// Find the maximum value among visible bars. Uses the DISPLAY totals so
			// the inflated "SET Allocation Pending" bar is fully accounted for and
			// never clipped by the dynamic scale.
			let maxVisibleValue = 0;
			for (let i = startIndex; i < endIndex; i++) {
				const teamTotal = teamDisplayTotals[mainChartData.labels[i]] || 0;
				if (teamTotal > maxVisibleValue) {
					maxVisibleValue = teamTotal;
				}
			}

			// Scale the y-axis to the tallest visible bar with only a small headroom
			// (6%) so bars fill most of the chart height instead of leaving a large
			// empty band on top. The grand-total label drawn above each bar keeps its
			// own cushion via layout.padding.top, so this stays clear of it.
			// minScale guards the all-tiny-bars / empty case.
			const minScale = 50;
			const calculatedMax = Math.max(maxVisibleValue * 1.06, minScale);

			setVisibleRange({ start: startIndex, end: endIndex });
			setDynamicMaxValue(calculatedMax);

			// Update chart scale if chart exists
			if (chartRef.current) {
				chartRef.current.options.scales.y.max = calculatedMax;

				// Recalculate step size
				const stepSize = calculatedMax / 8;
				chartRef.current.options.scales.y.ticks.stepSize = stepSize;

				chartRef.current.update("none"); // Update without animation for smooth scrolling
			}
		}, [mainChartData, teamDisplayTotals, barWidthPx]);

		// Initialize scale when component loads
		useEffect(() => {
			if (mainChartData) {
				calculateVisibleRange();
			}
		}, [mainChartData, calculateVisibleRange]);

		// Recompute the visible range / y-scale whenever the zoom level changes,
		// since a different bar width means a different set of bars is on screen.
		useEffect(() => {
			calculateVisibleRange();
		}, [zoom, calculateVisibleRange]);

		// Handle scroll events with optimized throttling
		const handleScroll = useCallback(
			(e) => {
				if (teamLabelsContainerRef.current && !window.syncingScroll) {
					window.syncingScroll = true;
					teamLabelsContainerRef.current.scrollLeft = e.target.scrollLeft;
					setCurrentScrollPosition(e.target.scrollLeft);

					// Throttle the scale calculation for performance - longer delay for smoother scrolling
					clearTimeout(window.scaleUpdateTimeout);
					window.scaleUpdateTimeout = setTimeout(() => {
						calculateVisibleRange();
					}, 1); // Increased from 50ms to 100ms for better performance

					setTimeout(() => {
						window.syncingScroll = false;
					}, 10);
				}
			},
			[calculateVisibleRange],
		);

		// Initialize visible range when data changes
		useEffect(() => {
			if (mainChartData && chartContainerRef.current) {
				setTimeout(() => {
					calculateVisibleRange();
				}, 100);
			}
		}, [mainChartData, calculateVisibleRange]);

		// Cleanup timeouts on unmount
		useEffect(() => {
			return () => {
				clearTimeout(window.scaleUpdateTimeout);
			};
		}, []);

		// Dynamic chart options with improved tooltips and data labels
		const chartOptions = {
			...baseChartOptions,
			layout: {
				padding: {
					// Zoom-responsive reserve so the down-left diagonal SE-team labels
					// stay fully visible; applied equally to the label row below so
					// bars and labels shift together and stay aligned.
					left: labelLeftReservePx,
					right: 24,
					top: 34, // headroom for the grand-total labels drawn above each bar
					bottom: 36,
				},
			},
			plugins: {
				...baseChartOptions.plugins,
				legend: { display: false },
				datalabels: {
					display: (context) => {
						// Overlap guard: only draw a segment's value when the segment is
						// tall enough to contain the label line, so values never collide on
						// small or many-segment stacks. The grand total is still drawn above
						// each bar, and the full per-segment breakdown is in the hover tooltip.
						const meta = context.chart?.getDatasetMeta?.(context.datasetIndex);
						const el = meta?.data?.[context.dataIndex];
						if (!el) return true;
						let h = 0;
						if (typeof el.base === "number" && typeof el.y === "number") {
							h = Math.abs(el.base - el.y);
						} else if (typeof el.height === "number") {
							h = el.height;
						}
						return h >= 16;
					},
					anchor: "center",
					align: "center",
					offset: 0,
					clamp: true,
					padding: { top: 2, bottom: 2, left: 5, right: 5 },
					font: {
						family: "MAN EUROPE_Bold",
						size: 14,
					},
					formatter: (value, context) => {
						// Use the REAL segment value (the chart value may be inflated for
						// the pending column) so the label always shows the true count.
						const realValue =
							orderedChartData?.datasets?.[context.datasetIndex]?.data?.[
								context.dataIndex
							] ?? value;
						// Only show label if value is greater than 0 and significant enough
						const total = teamGrandTotals[context.label] || 0;
						const percentage = total > 0 ? (realValue / total) * 100 : 0;

						// Show value only if it's more than 2% of the total or greater than 3
						if (realValue > 0 && (percentage > 2 || realValue > 3)) {
							return realValue.toLocaleString();
						}
						return "";
					},
					color: (context) => {
						// Pick a readable text colour from the segment's own background
						// brightness: white on dark/grey/blue segments (e.g. M #545454,
						// red #cc0000, navy #1e2832), dark on light segments (e.g. orange
						// #ff8c00). Computed from the actual colour so it stays correct if
						// the palette changes. Non-hex/gradient fallbacks default to white.
						const bg = context.dataset.backgroundColor;
						const hex =
							typeof bg === "string" && bg.startsWith("#") ? bg.slice(1) : null;
						if (!hex || (hex.length !== 3 && hex.length !== 6)) {
							return "#ffffff";
						}
						const full =
							hex.length === 3
								? hex
										.split("")
										.map((c) => c + c)
										.join("")
								: hex;
						// Choose black or white by whichever has the higher WCAG contrast
						// ratio against this segment's background — a proper luminance
						// check, not per-category hardcoding. Dark backgrounds (grey, red,
						// navy, black) get white; light ones (green, orange, yellow) get
						// black.
						const toLinear = (channel) => {
							const c = parseInt(channel, 16) / 255;
							return c <= 0.03928
								? c / 12.92
								: ((c + 0.055) / 1.055) ** 2.4;
						};
						const relativeLuminance =
							0.2126 * toLinear(full.slice(0, 2)) +
							0.7152 * toLinear(full.slice(2, 4)) +
							0.0722 * toLinear(full.slice(4, 6));
						// Contrast ratio vs white (L=1) and vs black (L=0).
						const contrastWithWhite = (1 + 0.05) / (relativeLuminance + 0.05);
						const contrastWithBlack = (relativeLuminance + 0.05) / (0 + 0.05);
						const preferWhite = contrastWithWhite >= contrastWithBlack;

						// Light background (green, orange, …) => black text. Unchanged.
						if (!preferWhite) {
							return "#000000";
						}

						// Dark background (red, grey, navy, …): white reads well only when it
						// actually sits inside the segment. On a very short segment the value
						// overflows onto the light chart background and disappears, so fall
						// back to black until the segment grows tall enough to contain it.
						// Measured from the drawn bar element, so it re-evaluates on every
						// render — i.e. live as zoom or data/filters change the segment size.
						const meta = context.chart?.getDatasetMeta?.(context.datasetIndex);
						const element = meta?.data?.[context.dataIndex];
						let segmentHeightPx = 0;
						if (element) {
							if (
								typeof element.base === "number" &&
								typeof element.y === "number"
							) {
								segmentHeightPx = Math.abs(element.base - element.y);
							} else if (typeof element.height === "number") {
								segmentHeightPx = element.height;
							}
						}
						// ~ one line of the 14px value label; below this the text can't fit
						// within the segment.
						const MIN_SEGMENT_HEIGHT_FOR_WHITE = 18;
						return segmentHeightPx >= MIN_SEGMENT_HEIGHT_FOR_WHITE
							? "#ffffff"
							: "#000000";
					},
					backgroundColor: (context) => {
						// Add a semi-transparent background for better readability
						const value = context.parsed;
						if (value > 0) {
							return "rgba(0, 0, 0, 0.3)";
						}
						return "transparent";
					},
					borderColor: (context) => {
						// Add a subtle border for contrast
						const backgroundColor = context.dataset.backgroundColor;
						if (typeof backgroundColor === "string") {
							if (
								backgroundColor.includes("rgba(30, 40, 50") ||
								backgroundColor.includes("rgba(204, 0, 0") ||
								backgroundColor.includes("rgba(107, 151, 0") ||
								backgroundColor.includes("rgba(84, 84, 84")
							) {
								return "rgba(255, 255, 255, 0.4)";
							} else {
								return "rgba(0, 0, 0, 0.4)";
							}
						}
						return "rgba(255, 255, 255, 0.4)";
					},
				},
				tooltip: {
					backgroundColor: "rgba(255, 255, 255, 0.98)",
					titleColor: "#1e2832",
					bodyColor: "#333",
					borderColor: "#cc0000",
					borderWidth: 2,
					cornerRadius: 8,
					displayColors: true,
					titleFont: {
						family: "MAN EUROPE_Bold",
						size: 14,
					},
					bodyFont: {
						family: "MAN EUROPE_Regular",
						size: 12,
					},
					padding: 12,
					mode: "index",
					intersect: false,
					callbacks: {
						title: (context) => {
							const rawLabel = context[0].label;
							const teamName = formatTeamName(rawLabel).replace(/\b\w/g, (c) =>
								c.toUpperCase(),
							);
							const grandTotal = teamGrandTotals[rawLabel] || 0;
							return `${teamName} (Total: ${grandTotal.toLocaleString()} parts)`;
						},
						label: (context) => {
							const purchaseTypeCode = context.dataset.label;
							const purchaseTypeMapping = {
								K: "Kaufteil – Purchase part",
								M: "Zusammembau der Montage - Assembly of assembly",
								N: "Nicht bezogen - Not related",
								T: "Nicht gelagertes Hausteil – Non stored part of the house",
								H: "Hausteil – House part",
							};
							const purchaseTypeLabel =
								purchaseTypeMapping[purchaseTypeCode] || purchaseTypeCode;
							// Use the REAL count (chart value may be inflated for the
							// pending column) so the tooltip shows the true part count.
							const count =
								orderedChartData?.datasets?.[context.datasetIndex]?.data?.[
									context.dataIndex
								] ?? context.raw;
							const teamName = context.label;
							const grandTotal = teamGrandTotals[teamName] || 0;
							const percentage =
								grandTotal > 0
									? ((count / grandTotal) * 100).toFixed(1)
									: "0.0";

							return `${purchaseTypeCode}: ${purchaseTypeLabel} (${count.toLocaleString()} parts, ${percentage}%)`;
						},
						afterBody: (context) => {
							if (context.length > 0) {
								const teamName = context[0].label;
								const grandTotal = teamGrandTotals[teamName] || 0;
								return [`\nGrand Total: ${grandTotal.toLocaleString()} parts`];
							}
							return [];
						},
					},
				},
			},
			scales: {
				x: {
					stacked: true,
					display: false,
					grid: {
						display: false,
					},
					ticks: {
						maxRotation: 0,
						minRotation: 0,
					},
				},
				y: {
					stacked: true,
					display: false,
					grid: {
						color: "rgba(0, 0, 0, 0.1)",
						drawOnChartArea: true,
						drawTicks: false,
					},
					beginAtZero: true,
					max: dynamicMaxValue,
					ticks: {
						stepSize: dynamicMaxValue / 8,
					},
				},
			},
			// Bars fill most of their per-category slot for a solid, consistent
			// width (the slot width itself is the zoom-controlled barWidthPx), with a
			// small, even gap between categories.
			barPercentage: 0.92,
			categoryPercentage: barWidthPx <= 60 ? 0.9 : 0.55,
			// Fully rectangular bars — no rounded corners.
			elements: {
				bar: {
					borderRadius: 0,
					borderSkipped: false,
				},
			},
			maintainAspectRatio: false,
			responsive: true,
			afterLayout: (chart) => {
				if (teamLabelsContainerRef.current && chartContainerRef.current) {
					const labelsContainer = teamLabelsContainerRef.current;
					const chartWidth = chart.chartArea.width;
					labelsContainer.style.width = `${chartWidth}px`;
					labelsContainer.scrollLeft = chartContainerRef.current.scrollLeft;
				}
			},
			animation: {
				onComplete: (chart) => {
					// Draw grand total labels at the top of each bar after animation completes
					if (
						!chart ||
						!chart.ctx ||
						!chart.data ||
						!chart.data.datasets ||
						!chart.data.labels
					) {
						return;
					}

					const ctx = chart.ctx;
					const datasets = chart.data.datasets;
					const meta = chart.getDatasetMeta(0);

					ctx.save();
					ctx.font = "12px MAN EUROPE_Bold";
					ctx.textAlign = "center";
					ctx.fillStyle = "#1e2832";

					chart.data.labels.forEach((label, index) => {
						const grandTotal = teamGrandTotals[label] || 0;
						if (grandTotal > 0) {
							// Calculate the top of the highest stack
							let stackTop = 0;
							datasets.forEach((dataset, datasetIndex) => {
								const datasetMeta = chart.getDatasetMeta(datasetIndex);
								if (
									datasetMeta &&
									datasetMeta.data &&
									datasetMeta.data[index]
								) {
									const value = dataset.data[index] || 0;
									if (value > 0) {
										stackTop += value;
									}
								}
							});

							// Get the pixel position for the bar
							const barMeta = chart.getDatasetMeta(0);
							if (barMeta && barMeta.data && barMeta.data[index]) {
								const x = barMeta.data[index].x;
								const yScale = chart.scales.y;
								if (yScale) {
									const y = yScale.getPixelForValue(stackTop) - 20; // Position above the bar

									// Draw grand total with improved styling
									const text = grandTotal.toLocaleString();
									const textWidth = ctx.measureText(text).width;

									// Background rectangle with rounded corners simulation
									ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
									ctx.fillRect(
										x - textWidth / 2 - 6,
										y - 10,
										textWidth + 12,
										18,
									);

									// Border with better styling
									ctx.strokeStyle = "#cc0000";
									ctx.lineWidth = 1.5;
									ctx.strokeRect(
										x - textWidth / 2 - 6,
										y - 10,
										textWidth + 12,
										18,
									);

									// Shadow effect
									ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
									ctx.fillRect(
										x - textWidth / 2 - 5,
										y - 9,
										textWidth + 10,
										16,
									);

									// Text with better contrast
									ctx.fillStyle = "#1e2832";
									ctx.font = "bold 11px MAN EUROPE_Bold";
									ctx.fillText(text, x, y + 2);
								}
							}
						}
					});
					ctx.restore();
				},
			},
			onClick: handleChartClick, // Add click handler for navigating to supply type page
		};

		return (
			<Box
				sx={{
					width: "100%",
					maxWidth: "100%",
					overflow: "hidden",
					backgroundColor: "#f0f0f0",
					minHeight: "100vh",
					boxSizing: "border-box", // Ensure padding is included in width calculations
				}}
			>
				<Grid container spacing={3} sx={{ mb: 4 }}>
					{["Total Parts", "Purchase Types", "SE Teams"].map((title, index) => (
						<Grid item xs={12} md={4} key={title}>
							<Paper
								data-tour-target={
									title === "Total Parts"
										? "sta-dash-total-parts"
										: title === "Purchase Types"
											? "sta-dash-purchase-types"
											: "sta-dash-se-teams"
								}
								elevation={2}
								sx={{ p: 3, backgroundColor: "#fff", borderRadius: "8px" }}
							>
								<Typography
									variant="h6"
									sx={{ mb: 2, fontFamily: "MAN EUROPE_Bold" }}
								>
									{title}
								</Typography>
								<Typography
									variant="h3"
									sx={{ fontFamily: "MAN EUROPE_Bold", color: "#1e2832" }}
								>
									{index === 0
										? totalParts.toLocaleString()
										: index === 1
											? Object.keys(purchaseTypeCounts).length
											: Object.keys(seTeamCounts).length}
								</Typography>
								<Typography variant="body2" sx={{ mt: 1, color: "#545454" }}>
									{index === 0
										? "Total number of parts in the tracking system"
										: index === 1
											? "Number of different purchase types"
											: "Number of SE teams"}
								</Typography>
							</Paper>
						</Grid>
					))}
				</Grid>{" "}
				<Paper
					elevation={3}
					sx={{ p: 3, backgroundColor: "#fff", borderRadius: "8px", mb: 4 }}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "flex-start",
							mb: 3,
						}}
					>
						<Box sx={{ flex: 1 }}>
							<Typography
								variant="h5"
								sx={{ fontFamily: "MAN EUROPE_Bold", color: "#1e2832", mb: 1 }}
							>
								SET & Supply Type Allocation
							</Typography>

							{/* Active Filters Display */}
							<Box
								sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
							>
								<Typography
									variant="body2"
									sx={{
										fontFamily: "MAN EUROPE_Regular",
										color: "#666",
										fontSize: "0.8rem",
									}}
								>
									Active Filters{" "}
									{totalActiveFilters > 0 && `(${totalActiveFilters})`}:
								</Typography>
								{activeFiltersInfo.length > 0 ? (
									<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
										{activeFiltersInfo.map((filter, index) => (
											<Box
												key={index}
												sx={{
													display: "flex",
													flexDirection: "column",
													gap: 0.3,
												}}
											>
												<Typography
													variant="caption"
													sx={{
														fontSize: "0.65rem",
														fontWeight: "bold",
														color: "#cc0000",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													{filter.label}:
												</Typography>
												<Box
													sx={{ display: "flex", gap: 0.3, flexWrap: "wrap" }}
												>
													{filter.values.map((value, valueIndex) => (
														<Chip
															key={valueIndex}
															label={
																filter.label === "Teams" && value === "-"
																	? "SET Allocation Pending"
																	: value
															}
															size="small"
															sx={{
																backgroundColor: "#cc0000",
																color: "white",
																fontSize: "0.65rem",
																height: "18px",
																fontFamily: "MAN EUROPE_Regular",
																"& .MuiChip-label": {
																	padding: "0 4px",
																},
															}}
														/>
													))}
												</Box>
											</Box>
										))}
									</Box>
								) : (
									<Typography
										variant="body2"
										sx={{
											color: "#999",
											fontSize: "0.75rem",
											fontFamily: "MAN EUROPE_Regular",
										}}
									>
										No active filters
									</Typography>
								)}
							</Box>
						</Box>
						{/* FilterSection: Modern, compact filter UI */}{" "}
						<FilterSection
							tempSelectedProject={tempSelectedProject}
							tempSelectedProgramm={tempSelectedProgramm}
							tempSelectedPurchaseType={tempSelectedPurchaseType}
							tempSelectedTeams={tempSelectedTeams}
							tempSelectedCategory={tempSelectedCategory}
							handleProjectChange={handleProjectChange}
							handleProgrammChange={handleProgrammChange}
							handlePurchaseTypeChange={handlePurchaseTypeChange}
							handleTeamChange={handleTeamChange}
							handleCategoryChange={handleCategoryChange}
							handleApplyFilters={handleApplyFilters}
							handleResetFilters={handleResetFilters}
							hasFiltersChanged={hasFiltersChanged}
							filterOptions={filterOptions}
							filteredSeTeams={filteredSeTeams}
						/>
					</Box>
					<Box sx={{ width: "100%" }}>
						{mainChartData && (
							<Box
								sx={{
									width: "100%",
									padding: "15px 0",
									backgroundColor: "#fff",
									position: "sticky",
									top: 0,
									zIndex: 2,
									borderBottom: "1px solid #eee",
									marginBottom: "10px",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									flexWrap: "wrap",
									gap: "12px",
								}}
							>
								<Box
									sx={{
										display: "flex",
										flexWrap: "wrap",
										gap: "15px",
										padding: "0 20px",
										flex: "1 1 auto",
										minWidth: 0,
									}}
								>
									{mainChartData.datasets.map((dataset, index) => (
										<Box
											key={index}
											sx={{
												display: "flex",
												alignItems: "center",
												marginRight: "15px",
											}}
										>
											<Box
												sx={{
													width: "12px",
													height: "12px",
													backgroundColor: dataset.backgroundColor,
													borderRadius: "2px",
													marginRight: "5px",
													flexShrink: 0,
												}}
											/>
											<Typography
												variant="body2"
												sx={{
													fontFamily: "MAN EUROPE_Bold",
													fontSize: "12px",
													whiteSpace: "nowrap",
												}}
											>
												{dataset.label === "K"
													? "K: Purchase part"
													: dataset.label === "M"
														? "M: Assembly at MAN"
														: dataset.label === "N"
															? "N: Nicht bezogen - Not related"
															: dataset.label === "T"
																? "T: Nicht gelagertes Hausteil – Non stored part of the house"
																: dataset.label === "H"
																	? "H: Inhouse part"
																	: dataset.label}
											</Typography>
										</Box>
									))}
								</Box>

								{/* Zoom / Fit-all controls. View-only — they scale the chart's
								    horizontal spacing, never the data or categories. */}
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										padding: "0 20px",
										flexShrink: 0,
									}}
								>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											border: "1px solid #d0d0d0",
											borderRadius: "6px",
											overflow: "hidden",
										}}
									>
										<Button
											onClick={zoomOut}
											disabled={zoom <= MIN_ZOOM}
											aria-label="Zoom out"
											sx={{
												minWidth: "34px",
												padding: "4px 0",
												color: "#333",
												borderRadius: 0,
												fontSize: "18px",
												lineHeight: 1,
											}}
										>
											−
										</Button>
										<Typography
											sx={{
												minWidth: "52px",
												textAlign: "center",
												fontFamily: "MAN EUROPE_Bold",
												fontSize: "13px",
												color: "#333",
												userSelect: "none",
												borderLeft: "1px solid #eee",
												borderRight: "1px solid #eee",
											}}
										>
											{Math.round(zoom)}%
										</Typography>
										<Button
											onClick={zoomIn}
											disabled={zoom >= MAX_ZOOM}
											aria-label="Zoom in"
											sx={{
												minWidth: "34px",
												padding: "4px 0",
												color: "#333",
												borderRadius: 0,
												fontSize: "18px",
												lineHeight: 1,
											}}
										>
											+
										</Button>
									</Box>
									<Button
										onClick={fitAll}
										variant="outlined"
										sx={{
											textTransform: "none",
											fontFamily: "MAN EUROPE_Bold",
											fontSize: "12px",
											padding: "4px 12px",
											color: "#333",
											borderColor: "#d0d0d0",
											"&:hover": { borderColor: "#aaa", backgroundColor: "#f7f7f7" },
										}}
									>
										Fit all
									</Button>
								</Box>
							</Box>
						)}
						<Box
							sx={{
								height: `${cardHeightPx}px`,
								width: "100%",
								position: "relative",
								border: "1px solid #eee",
								borderRadius: "4px",
								overflow: "hidden",
								display: "flex",
							}}
						>
							<Box
								sx={{
									position: "absolute",
									left: 0,
									top: 0,
									bottom: `${chartBottomReservePx}px`,
									width: "80px",
									backgroundColor: "#fff",
									borderRight: "1px solid #eee",
									zIndex: 10,
									display: "flex",
									flexDirection: "column",
									padding: "20px 5px 20px 10px",
								}}
							>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column-reverse",
										justifyContent: "flex-start",
										alignItems: "flex-end",
										height: "100%",
										fontSize: "11px",
										fontFamily: "MAN EUROPE_Regular",
										color: "#666",
										paddingTop: "20px",
									}}
								>
									{mainChartData &&
										(() => {
											const maxValue = dynamicMaxValue;
											const ticks = [];
											const tickCount = 6;
											for (let i = 0; i <= tickCount; i++) {
												ticks.push(Math.round((maxValue / tickCount) * i));
											}
											return ticks.map((tick, index) => (
												<Box
													key={`${tick}-${index}`}
													sx={{
														textAlign: "right",
														width: "100%",
														height: `${100 / tickCount}%`,
														display: "flex",
														alignItems: "flex-end",
														justifyContent: "flex-end",
														paddingBottom: index === 0 ? "0px" : "0px",
													}}
												>
													{tick}
												</Box>
											));
										})()}
								</Box>
								<Box
									sx={{
										position: "absolute",
										left: "15px",
										top: "50%",
										right: "30%",
										transform: "translateY(-50%) rotate(-90deg)",
										transformOrigin: "center",
										fontSize: "14px",
										fontFamily: "MAN EUROPE_Bold",
										color: "#333",
										whiteSpace: "nowrap",
									}}
								>
									Number of Parts
								</Box>
							</Box>
							<Box
								ref={teamLabelsContainerRef}
								sx={{
									position: "absolute",
									bottom: "40px",
									left: "80px",
									right: 0,
									height: `${labelBandHeightPx}px`,
									backgroundColor: "#fff",
									borderTop: "1px solid #eee",
									zIndex: 10,
									overflowX: "auto",
									overflowY: "hidden",
									"&::-webkit-scrollbar": { display: "none" },
									scrollbarWidth: "none",
									msOverflowStyle: "none",
								}}
							>
								{" "}
								<Box
									sx={{
										display: "flex",
										minWidth: "100%",
										width: mainChartData
											? `${Math.max(mainChartData.labels.length * barWidthPx, 800) + labelLeftReservePx}px`
											: "100%",
										height: "100%",
										alignItems: "flex-start",
										// Match the chart canvas's zoom-responsive left reserve so
										// each label stays aligned with its bar and the leftmost
										// diagonal label never clips.
										padding: `4px 30px 4px ${20 + labelLeftReservePx}px`,
									}}
								>
									{mainChartData?.labels.map((teamName, index) => (
										<Box
											key={index}
											sx={{
												width: `${barWidthPx}px`,
												minWidth: `${barWidthPx}px`,
												flexShrink: 0,
												height: "100%",
												position: "relative",
												overflow: "visible",
											}}
										>
											{/* Diagonal category label (-45deg). Full name preserved and wrapped at
											    word boundaries into 2-3 balanced lines (never a single-letter
											    line), anchored by its top-right corner at the bar centre so it
											    fans down-left and stays aligned with its bar. */}
											<span
												style={{
													position: "absolute",
													top: "8px",
													right: "50%",
													transform: "rotate(-45deg)",
													transformOrigin: "right top",
													whiteSpace: "pre-line",
													textAlign: "right",
													lineHeight: 1.1,
													fontSize: `${labelFontPx}px`,
													fontFamily: "MAN EUROPE_Bold",
													color: "#333",
													pointerEvents: "none",
												}}
											>
												{vpWrapLabel(
															formatTeamName(teamName).replace(/\b\w/g, (c) =>
																c.toUpperCase(),
															),
														)
															.split("\n")
															.map((line, i) => (
																<div
																	key={i}
																	style={{
																		marginTop: i === 0 ? 0 : "4px",
																		whiteSpace: "nowrap",
																	}}
																>
																	{line}
																</div>
															))}
											</span>
										</Box>
									))}
								</Box>
							</Box>
							<Box
								sx={{
									position: "absolute",
									bottom: 0,
									left: "80px",
									right: 0,
									height: "40px",
									backgroundColor: "#fff",
									zIndex: 10,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									fontSize: "14px",
									fontFamily: "MAN EUROPE_Bold",
									color: "#333",
								}}
							>
								SE-Team
							</Box>
							<Box
								ref={chartContainerRef}
								sx={{
									marginLeft: "80px",
									marginBottom: `${chartBottomReservePx}px`,
									width: "calc(100% - 80px)",
									height: `calc(100% - ${chartBottomReservePx}px)`,
									overflowX: "auto",
									overflowY: "hidden",
									"&::-webkit-scrollbar": { height: "8px" },
									"&::-webkit-scrollbar-track": {
										background: "#f1f1f1",
										borderRadius: "4px",
									},
									"&::-webkit-scrollbar-thumb": {
										background: "#cc0000",
										borderRadius: "4px",
										"&:hover": { background: "#aa0000" },
									},
								}}
								onScroll={handleScroll}
							>
								{isChartLoading ? (
									<Box
										display="flex"
										justifyContent="center"
										alignItems="center"
										height="100%"
										sx={{
											minHeight: "360px",
											backgroundColor: "rgba(255, 255, 255, 0.9)",
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											zIndex: 20,
											borderRadius: "4px",
										}}
									>
										<Box sx={{ textAlign: "center" }}>
											<CircularProgress
												size={50}
												thickness={4}
												sx={{ color: "#cc0000", mb: 2 }}
											/>
											<Typography
												variant="body2"
												sx={{ color: "#666", fontFamily: "MAN EUROPE_Regular" }}
											>
												Loading chart data...
											</Typography>
										</Box>
									</Box>
								) : mainChartData ? (
									<div
										style={{
											minWidth: "100%",
											// Grow the scroll content by the same left reserve added to
											// the canvas (layout.padding.left) so the plot area keeps its
											// full labels*barWidthPx width — bars stay one-per-slot and
											// aligned with the label row, just shifted right by the reserve.
											width: `${Math.max(mainChartData.labels.length * barWidthPx, 800) + labelLeftReservePx}px`, // Scales with zoom; scrolls horizontally when it exceeds the container
											maxWidth: "none", // Allow horizontal scrolling when needed
											height: "100%",
											minHeight: "360px",
											padding: "8px 30px 0px 20px",
										}}
									>
										{" "}
										<Bar
											ref={chartRef}
											data={mainChartData}
											options={chartOptions}
										/>
									</div>
								) : (
									<Box
										display="flex"
										justifyContent="center"
										alignItems="center"
										height="100%"
										sx={{ minHeight: "360px" }}
									>
										<Typography variant="body1" color="text.secondary">
											No data available for chart
										</Typography>
									</Box>
								)}
							</Box>
						</Box>
					</Box>
				</Paper>
				{/* Chart Click Modal - Similar to VFF Dashboard */}
				{modalOpen && (
					<Box
						sx={{
							position: "fixed",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: "rgba(0, 0, 0, 0.7)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 9999,
						}}
						onClick={() => setModalOpen(false)}
					>
						<Box
							sx={{
								backgroundColor: "white",
								borderRadius: "12px",
								padding: "32px",
								maxWidth: "500px",
								width: "90%",
								boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
								textAlign: "center",
								position: "relative",
							}}
							onClick={(e) => e.stopPropagation()}
						>
							{/* Close button */}
							<Box
								sx={{
									position: "absolute",
									top: "12px",
									right: "16px",
									cursor: "pointer",
									fontSize: "24px",
									color: "#666",
									"&:hover": { color: "#333" },
								}}
								onClick={() => setModalOpen(false)}
							>
								×
							</Box>

							<Typography
								variant="h5"
								sx={{
									fontFamily: "MAN EUROPE_Bold",
									color: "#1e2832",
									mb: 3,
								}}
							>
								Supply Type Details
							</Typography>
							<Typography
								variant="body1"
								sx={{
									fontFamily: "MAN EUROPE_Regular",
									color: "#333",
									mb: 1,
								}}
							>
								<strong>SE Team:</strong>{" "}
								{formatTeamName(modalData.seTeam).replace(/\b\w/g, (c) =>
									c.toUpperCase(),
								)}
							</Typography>

							<Typography
								variant="body1"
								sx={{
									fontFamily: "MAN EUROPE_Regular",
									color: "#333",
									mb: 1,
								}}
							>
								<strong>Purchase Type:</strong> {modalData.purchaseType}
							</Typography>

							<Typography
								variant="body1"
								sx={{
									fontFamily: "MAN EUROPE_Regular",
									color: "#333",
									mb: 3,
								}}
							>
								<strong>Parts Count:</strong>{" "}
								{modalData.count?.toLocaleString()}
							</Typography>

							<Box
								sx={{
									display: "flex",
									gap: 2,
									justifyContent: "center",
									mt: 4,
								}}
							>
								<Button
									variant="outlined"
									onClick={() => setModalOpen(false)}
									sx={{
										fontFamily: "MAN EUROPE_Bold",
										color: "#666",
										borderColor: "#ddd",
										"&:hover": {
											borderColor: "#999",
											backgroundColor: "#f5f5f5",
										},
									}}
								>
									Close
								</Button>

								<Button
									variant="contained"
									onClick={handleOpenSupplyTypePage}
									sx={{
										backgroundColor: "#cc0000",
										fontFamily: "MAN EUROPE_Bold",
										"&:hover": {
											backgroundColor: "#aa0000",
										},
									}}
								>
									View Details in Supply Type Page
								</Button>
							</Box>
						</Box>
					</Box>
				)}
			</Box>
		);
	},
);

export default SupplyTypeAllocationDashboard;
